import { createTelemetry, createTelemetryObject } from '@shared/core';
import type { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import type { getTimezone } from '@apps/acquisitionFunnels/createWedding/utils/getTimezone';
import type { TelemetryAuthMode } from '@apps/acquisitionFunnels/createWedding/CreateWedding.types';

type PagesForTelemetry = 'createAccount' | 'signIn' | 'forgotPassword' | 'tyingTheKnot' | 'createWeddingForm';

interface WebsiteCreatedProps
  extends Readonly<{
    website: string;
    eventId: string;
    email: string;
    adminOrOwnerEventCount?: number;
    venueLocationProvided: boolean;
    venueLocationSkipped: boolean;
    lastNameProvided: boolean;
    // Venue country (ISO alpha-2, e.g. GB). Omitted from the event when the venue is skipped (null is stripped).
    eventCountryCode: string | null;
  }> {}

const pageTracking: Record<PagesForTelemetry, PageFunctionType> = {
  createAccount: () => ({ category: 'CreateWeddingV2', page: 'signup', pagePrefix: 'marketing', version: 2 }),
  signIn: () => ({ category: 'CreateWeddingV2', page: 'login', pagePrefix: 'marketing', version: 2 }),
  forgotPassword: () => ({ category: 'CreateWeddingV2', page: 'forgotPassword', pagePrefix: 'marketing', version: 2 }),
  tyingTheKnot: () => ({ category: 'CreateWeddingV2', page: 'tyingTheKnot', pagePrefix: 'marketing' }),
  createWeddingForm: () => ({ category: 'CreateWeddingV2', page: 'weddingDetailsForm', pagePrefix: 'marketing', version: 2 }),
};

const CREATE_WEDDING_CTA = 'CreateWeddingCTA';

export const createWeddingTelemetry = createTelemetryObject({
  actions: {
    inputHandler: (fieldIdentifier: string, inputAction: 'onFocus' | 'onChange') => ({
      action: 'FormFieldInteracted',
      category: 'CreateWeddingV2',
      label: fieldIdentifier,
      extraInfo: {
        inputAction,
      },
    }),
    passwordClicked: (authMode: TelemetryAuthMode) => ({ action: 'Password', actionType: 'click', category: 'CreateWeddingV2', label: authMode }),
    emailClicked: (authMode: TelemetryAuthMode) => ({ action: 'Email', actionType: 'click', category: 'CreateWeddingV2', label: authMode }),
    createAccountClicked: () => ({
      action: CREATE_WEDDING_CTA,
      actionType: 'click',
      category: 'CreateWeddingV2',
      extraInfo: {
        CtaName: 'EmailPassword',
      },
    }),
    logoutClicked: () => ({ action: 'Logout', actionType: 'click', category: 'CreateWeddingV2' }),
    trackError: (action: string, error: unknown) => ({ action, actionType: 'error', category: 'CreateWeddingV2', extraInfo: { error } }),
    weddingCreated: ({ website, ...restProps }: WebsiteCreatedProps) => ({
      action: 'Publish',
      label: website,
      category: 'CreateWeddingV2',
      extraInfo: { orderId: Date.now(), ...restProps },
    }),
    timezoneRegistered: (info: Awaited<ReturnType<typeof getTimezone>>) => ({ action: 'TimezoneRegistered', category: 'CreateWeddingV2', extraInfo: info }),
    dateSet: (eventDate: string, dateLabel: string, durationDays: number | undefined) => ({
      action: 'DateSet',
      category: 'CreateWeddingV2',
      label: dateLabel,
      extraInfo: { eventDate, durationDays },
    }),
    submitDetailsClicked: (userEmail: string) => ({
      action: CREATE_WEDDING_CTA,
      actionType: 'click',
      label: 'continue',
      category: 'CreateWeddingV2',
      extraInfo: { email: userEmail, CtaName: 'SubmitDetails' },
    }),
    emailUnavailable: (userId: string, eventType: string) => ({
      action: 'UserEmailUnavailable',
      category: 'CreateWeddingV2',
      extraInfo: { userId, eventType },
    }),
    firstNamesSubmitted: (userEmail: string) => ({
      action: CREATE_WEDDING_CTA,
      actionType: 'click',
      label: 'continue',
      category: 'CreateWeddingV2',
      extraInfo: { email: userEmail, CtaName: 'FirstNames' },
    }),
    weddingDetailsSubmitted: (userEmail: string) => ({
      action: CREATE_WEDDING_CTA,
      actionType: 'click',
      label: 'continue',
      category: 'CreateWeddingV2',
      extraInfo: { email: userEmail, CtaName: 'WeddingDetails' },
    }),
    maliciousRedirect: maliciousHref => ({
      action: 'maliciousRedirect',
      category: 'CreateWeddingV2',
      extraInfo: {
        maliciousHref,
      },
    }),
    findACoupleClicked: () => ({
      action: 'FindACouple',
      category: 'CreateWeddingV2',
      actionType: 'click',
      label: 'find a couple',
      page: 'CreateWeddingV2',
      pagePrefix: 'marketing',
    }),
    socialIntercept: socialProvider => ({
      action: 'CreateEventWithEmailPassword',
      actionType: 'click',
      label: 'Create event with email and password',
      category: 'CreateWeddingV2',
      pagePrefix: 'marketing',
      page: 'social_intercept',
      extraInfo: { accountType: socialProvider },
    }),
  },
  pages: pageTracking,
});

const { TelemetryProvider, useTelemetry: useCreateWeddingTelemetry } = createTelemetry(createWeddingTelemetry);

export { TelemetryProvider, useCreateWeddingTelemetry };
